import { enderecoDeArquivo } from '@/shared/api/client';
import type { PolicyInstallment, QuoteInstallmentList } from '@/shared/api/resources';

/**
 * Boleto anexado visto por quem baixa (P-BOL; ADR 0071).
 *
 * O boleto e emitido fora do SeguraCore e anexado pela seguradora a cada parcela, na
 * cotacao ou na apolice. A corretora ve e baixa o boleto das proprias cotacoes e
 * apolices, e nao anexa, nao troca nem retira. A geracao automatica do boleto fica para
 * quando houver integracao bancaria ou com o ERP.
 *
 * A tela decide o botao pelas parcelas, que nao entregam o arquivo; o endereco
 * temporario so e pedido no clique, porque cada entrega entra na trilha.
 */

/** O motivo do botao desligado quando nao ha boleto para baixar. */
export const MOTIVO_SEM_BOLETO = 'Boleto ainda nao anexado pela seguradora.';

/** Motivo da cotacao emitida: o boleto passou para a apolice. */
export const MOTIVO_COTACAO_EMITIDA =
  'Depois da emissao, o boleto e o da apolice: baixe pela apolice.';

/** Motivo da cotacao recusada ou cancelada: nao ha premio a pagar. */
export const MOTIVO_COTACAO_ENCERRADA =
  'Cotacao encerrada sem emissao: o boleto da cotacao nao vale mais.';

/**
 * Por que a cotacao encerrada nao entrega o boleto dela, ou null na cotacao aberta.
 *
 * Depois da emissao, parcelas e boletos sao os da apolice: ela copia o boleto e pode
 * troca-lo, e o da cotacao fica congelado. A cotacao recusada ou cancelada nao tem
 * premio a pagar. O servidor responde o mesmo motivo (R5-A08).
 */
export function motivoDaCotacaoEncerrada(etapa: string | undefined): string | null {
  if (etapa === 'emitida') return MOTIVO_COTACAO_EMITIDA;
  if (etapa === 'declinada') return MOTIVO_COTACAO_ENCERRADA;
  return null;
}

/**
 * Alguma parcela da cotacao tem boleto anexado, com a empresa no modo de anexo e a
 * cotacao ainda aberta.
 */
export function temBoletoNaCotacao(lista: QuoteInstallmentList | undefined): boolean {
  return (
    lista?.billet_mode === 'anexo' &&
    motivoDaCotacaoEncerrada(lista.stage) === null &&
    lista.items.some((parcela) => parcela.billet !== null)
  );
}

/** Motivo da parcela cancelada: o boleto dela nao e entregue. */
export const MOTIVO_PARCELA_CANCELADA =
  'Parcela cancelada: o boleto dela nao e entregue.';

/** Motivo da apolice cancelada, o mesmo do servidor. */
export const MOTIVO_APOLICE_CANCELADA =
  'A apolice esta cancelada: os boletos dela nao sao entregues.';

/**
 * O boleto da parcela pode ser entregue: a parcela tem boleto e nao esta cancelada. A
 * parcela paga continua entregue, como comprovante, ate o dono decidir (R5-M19).
 */
export function boletoEntregavel(parcela: PolicyInstallment): boolean {
  return parcela.billet_document_id !== null && parcela.status !== 'cancelada';
}

/**
 * Alguma parcela da apolice tem boleto a entregar, com a empresa no modo de anexo. A
 * apolice cancelada nao entrega boleto nenhum: o premio dela deixou de ser cobrado.
 */
export function temBoletoNaApolice(
  modo: unknown,
  parcelas: PolicyInstallment[] | undefined,
  situacaoDaApolice?: string,
): boolean {
  return (
    modo === 'anexo' &&
    situacaoDaApolice !== 'cancelada' &&
    (parcelas ?? []).some(boletoEntregavel)
  );
}

/** Abre o arquivo do boleto pelo endereco temporario, em outra aba. */
export function abrirBoleto(url: string) {
  window.open(enderecoDeArquivo(url), '_blank', 'noopener,noreferrer');
}

/** A chave da apolice nascida da cotacao, a mesma na linha do tempo e no dialogo. */
export function chaveDaApoliceDaCotacao(operationId: string) {
  return ['policies', 'da-cotacao', operationId] as const;
}

/** A chave do cache das parcelas da cotacao, dentro da chave do financeiro. */
export function chaveDasParcelasDaCotacao(operationId: string) {
  return ['operations', operationId, 'financial', 'installments'] as const;
}
